import type { Envelope } from "./envelope";

/** A carrier that delivers an outbound envelope to the embedding host. */
export interface Adapter {
  send(env: Envelope): void;
}

/** The subset of host-injected globals the bridge probes to detect its carrier. */
interface HostGlobals {
  webkit?: {
    messageHandlers?: {
      tigerAgentBridge?: { postMessage(env: Envelope): void };
    };
  };
  TigerAgentBridge?: { postMessage?: (json: string) => void };
  parent?: Window;
}

/** iOS WKWebView: `WKScriptMessageHandler` registered under `tigerAgentBridge`. */
export function iosAdapter(win: HostGlobals): Adapter {
  return {
    send(env) {
      win.webkit!.messageHandlers!.tigerAgentBridge!.postMessage(env);
    },
  };
}

/** Android WebView: `@JavascriptInterface` exposed as `TigerAgentBridge` (strings only). */
export function androidAdapter(win: HostGlobals): Adapter {
  return {
    send(env) {
      win.TigerAgentBridge!.postMessage!(JSON.stringify(env));
    },
  };
}

/** Web parent iframe host: `postMessage` to the parent window. */
export function webParentAdapter(parent: Pick<Window, "postMessage">): Adapter {
  return {
    send(env) {
      parent.postMessage(env, "*");
    },
  };
}

/**
 * Detect the host carrier at runtime. Returns `null` for standalone/dev (no host) — the
 * bridge core then rejects host calls with `unsupported_event` rather than hanging.
 */
export function pickAdapter(win: Window): Adapter | null {
  const w = win as unknown as HostGlobals;
  if (w.webkit?.messageHandlers?.tigerAgentBridge) {
    return iosAdapter(w);
  }
  if (typeof w.TigerAgentBridge?.postMessage === "function") {
    return androidAdapter(w);
  }
  if (w.parent && w.parent !== win) {
    return webParentAdapter(w.parent);
  }
  return null;
}

export interface WebParentMessageOptions {
  receive: (raw: unknown) => void;
  /**
   * Origins permitted to push into the SPA. Empty/omitted accepts any origin — the app
   * relies on other mechanisms for host authentication.
   */
  allowedOrigins?: string[];
  /** The expected parent window; guards against same-origin sibling frames. */
  parentSource: unknown;
}

/**
 * Inbound guard for the web-parent carrier: forward a `message` event to `receive` only
 * when it comes from the parent window (and an allowlisted origin, when one is set).
 */
export function handleWebParentMessage(
  ev: Pick<MessageEvent, "data" | "origin" | "source">,
  opts: WebParentMessageOptions,
): void {
  if (ev.source !== opts.parentSource) return;
  if (opts.allowedOrigins?.length && !opts.allowedOrigins.includes(ev.origin)) return;
  opts.receive(ev.data);
}
